import { useEffect, useState, useCallback } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import StaffTeamPanel from "@/components/StaffTeamPanel";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";

/** NTN staff team management page — invite / role / status / removal via
 *  StaffTeamPanel (server-authorized in routes/team.py). */
export default function AdminStaffPage() {
  const [staff, setStaff] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    apiClient.get("/team/users")
      .then(({ data }) => setStaff(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <AppShell>
      <div className="mb-8">
        <div className="font-overline text-[#4A5568] mb-2">Staff console</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#0A192F]" data-testid="admin-staff-title">
          NTN team
        </h1>
        <p className="text-[#4A5568] mt-2 max-w-2xl">
          The internal NTN analyst and admin team. Invite, promote, deactivate, or remove staff members below.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <StaffTeamPanel staff={staff} onChanged={load} />
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="h-5 w-5 text-[#0A192F]" />
              <h3 className="font-serif text-xl text-[#0A192F]">All staff accounts</h3>
            </div>
            {error && <div className="text-[#EF4444] text-sm mb-3" data-testid="admin-staff-error">{error}</div>}
            <ul className="divide-y divide-[#0A192F]/8">
              {staff.length === 0 ? (
                <li className="py-6 text-center text-sm text-[#4A5568]">No staff users yet.</li>
              ) : staff.map((u) => (
                <li key={u.id} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-staff-${u.id}`}>
                  <div className="min-w-0">
                    <div className="text-[#0A192F] truncate text-sm">{u.name}</div>
                    <div className="text-xs text-[#4A5568] font-mono truncate">{u.email}</div>
                  </div>
                  <Badge variant="outline" className="rounded-sm text-[10px] uppercase font-mono border-[#0A192F]/15">
                    {u.is_master ? "master" : u.account_role}
                  </Badge>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-[#0A192F]/8 text-xs text-[#4A5568] font-mono">
              {staff.length} total
            </div>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
